import styled, { css, DefaultTheme } from 'styled-components';

import { BaseStyle, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import { WithHidden } from '~/core/styleTypes';
import { LeadingTeam } from '~/views/components/gameScore/styles';

import { MobileDayOfMonth, MobileGameInfo, MobileTeamDisplay } from '../mobileDay/styles';

type ColorProps = {
    singleGameDay: boolean;
    hasGame?: boolean;
    neutralSite?: boolean;
    teamAbbrev?: ShortCodeEnums.TeamTriCode;
    isHome?: boolean;
};

type WithColor = {
    $colorProps: ColorProps;
};

const getBackgroundColor = (theme: DefaultTheme, props: ColorProps) => {
    if (props.hasGame) {
        return props.isHome &&
            props.singleGameDay &&
            !props.neutralSite &&
            props.teamAbbrev &&
            theme.Team[props.teamAbbrev]
            ? theme.Team[props.teamAbbrev].primary
            : theme.Surface.primary;
    }

    return theme.Surface.secondary;
};

const getColor = (theme: DefaultTheme, props: ColorProps) =>
    props.isHome && props.singleGameDay && !props.neutralSite && props.teamAbbrev && theme.Team[props.teamAbbrev]
        ? theme.Team[props.teamAbbrev].textOnPrimary
        : theme.Text.primary;

export const LiveGame = styled.div<{ $isFrench: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['16b']}
    width: 36px;
    height: 16px;
    text-align: right;
    text-transform: uppercase;

    ${({ $isFrench }) =>
        $isFrench &&
        css`
            font-size: 8px;
            width: fit-content;

            ${BaseStyle.Mixins.screenSizes.large} {
                ${BaseStyle.Mixins.fontStyles.ui['12b']}
            }
        `}
`;

export const TopBar = styled.div<WithColor & WithHidden>`
    ${BaseStyle.Mixins.fontStyles.ui['16m']}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    flex-direction: row;
    flex: 1;
    width: 100%;
    max-height: 16px;
    text-align: left;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    padding: 0 16px;
    margin-bottom: ${({ $colorProps }) => ($colorProps.hasGame ? '0' : '100px')};
    color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};

    ${LiveGame} {
        color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    }

    @media print {
        && {
            ${BaseStyle.Mixins.fontStyles.ui['20m']}
            margin-top: 15px;
        }
    }
`;

export const ImageAndInfoContainer = styled.div<{ $postGame: boolean } & WithHidden>`
    ${BaseStyle.Mixins.gaps.small}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    flex-direction: column;
    width: 100%;
    height: 106px;
    align-items: center;
    margin-top: 4px;

    @media print {
        && {
            ${BaseStyle.Mixins.gaps.small}
        }
    }
`;

export const InformationContainer = styled.div`
    ${BaseStyle.Mixins.gaps.tiny}
    display: flex;
    flex-direction: column;
    height: 26px;
    width: 100%;
`;

export const TeamLogo = styled.div`
    min-height: 72px;
    min-width: 72px;
    display: flex;
    justify-content: center;
    border-radius: 50%;

    .team-display {
        justify-content: center;
    }

    .team-logo {
        height: 100%;
        width: 56px;
    }

    @media print {
        min-height: 90px;
        min-width: 90px;

        .team-logo {
            height: 100%;
            width: 76px;
        }
    }
`;

export const DayContainer = styled.div<WithColor>`
    display: flex;
    flex-direction: column;
    height: 51px;
    border: solid 1px ${({ $colorProps, theme }) => ($colorProps.hasGame ? theme.Surface.border : 'none')};
    border-radius: 8px;
    background-color: ${({ theme, $colorProps }) => getBackgroundColor(theme, $colorProps)};
    text-align: left;
    align-items: center;

    svg {
        fill: ${({ theme, $colorProps }) =>
            $colorProps.teamAbbrev ? theme.Team[$colorProps.teamAbbrev].primary : 'none'};
    }

    ${MobileDayOfMonth}, ${MobileTeamDisplay}, ${MobileGameInfo} {
        color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    }

    ${TeamLogo} {
        border: 1px solid
            ${({ $colorProps, theme }) => ($colorProps.isHome ? theme.Surface.Opposite.border : theme.Surface.border)};
        background-color: ${({ $colorProps, theme }) =>
            $colorProps.isHome ? theme.Surface.Opposite.secondary : theme.Surface.secondary};
    }

    ${BaseStyle.Mixins.screenSizes.medium} {
        height: 184px;
        border-radius: 8px;
        max-width: 168px;
    }

    @media print {
        border: solid 1px ${({ theme }) => theme.Surface.border};
        min-width: 168px;
        min-height: 184px;
        border-radius: 8px;
        background-color: ${({ theme, $colorProps }) =>
            $colorProps.hasGame ? getBackgroundColor(theme, $colorProps) : theme.Surface.primary};

        .rt-td {
            border: solid 1px ${({ theme }) => theme.Surface.border};
            min-width: 168px;
            min-height: 184px;
            border-radius: 8px;
            background-color: ${({ theme, $colorProps }) =>
                $colorProps.hasGame ? getBackgroundColor(theme, $colorProps) : theme.Surface.primary};
        }
    }
`;

export const GameTime = styled.div<WithColor & WithHidden>`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    width: 100%;
    height: 14px;
    text-align: center;
    justify-content: center;
    color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};

    ${BaseStyle.Mixins.screenSizes.large} {
        ${BaseStyle.Mixins.fontStyles.ui['14b']}
    }

    @media print {
        ${BaseStyle.Mixins.fontStyles.ui['24b']}
    }
`;

export const LiveGameScore = styled.div<WithColor & WithHidden>`
    ${BaseStyle.Mixins.fontStyles.ui['12m']}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    flex-direction: row;
    width: 100%;
    height: 14px;
    justify-content: center;
    align-items: center;
    color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};

    ${LeadingTeam} {
        color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        ${BaseStyle.Mixins.fontStyles.ui['14m']}
    }
`;

export const FinalGameScore = styled.div<WithColor & WithHidden>`
    ${BaseStyle.Mixins.fontStyles.ui['14b']}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    width: 100%;
    height: 26px;
    justify-content: center;
    color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    text-align: center;
    align-items: center;

    ${BaseStyle.Mixins.screenSizes.large} {
        ${BaseStyle.Mixins.fontStyles.ui['20b']}
    }

    @media print {
        ${BaseStyle.Mixins.fontStyles.ui['24b']}
    }
`;

export const ScrollingText = styled.div<WithColor & { $shouldScroll?: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['10m']}
    display: flex;
    min-height: 10px;
    width: ${({ $shouldScroll }) => ($shouldScroll ? 'fit-content' : '100%')};
    min-width: ${({ $shouldScroll }) => ($shouldScroll ? '100%' : '')};

    color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    line-height: 10px;
    text-align: center;
    justify-content: center;
    white-space: nowrap;

    /* animation properties. source: https://blog.hubspot.com/website/scrolling-text-css */
    /* we translate right using cqw (container query width, the width of BottomBar) rather than the text's own width to keep long text from being placed way right of the visible area. source: https://stackoverflow.com/a/76347984 */
    ${({ $shouldScroll }) =>
        $shouldScroll
            ? css`
                transform: translateX(100cqw);
                animation: marquee-animation 5s linear infinite;
    
                @keyframes marquee-animation {
                    from {
                        transform: translateX(100cqw);
                    }
                    
                    to {
                        transform: translateX(-100%);
                    }`
            : ''};

    @media print {
        display: none;
    }
`;

export const Broadcasters = styled.div<WithHidden>`
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'initial')};
    min-height: 10px;
    width: 100%;
    overflow: hidden;
    container-type: size;

    @media print {
        display: none;
    }
`;

export const Divider = styled.div`
    width: 2px;
    height: 16px;
`;

export const LinkContainer = styled.a`
    width: 100%;
    height: 32px;
    display: flex;
    justify-content: center;
    && {
        align-items: center;
    }
`;

export const LinksContainer = styled.div<WithHidden & { $shrinkLinks: boolean }>`
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    height: 32px;
    width: 100%;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    svg {
        ${BaseStyle.Mixins.fontStyles.ui['24m']}
    }

    ${Divider}:last-child {
        display: none;
    }

    ${LinkContainer}:first-of-type {
        border-bottom-left-radius: 6px;
    }

    ${LinkContainer}:last-of-type {
        border-bottom-right-radius: 6px;
    }

    ${({ $shrinkLinks }) =>
        $shrinkLinks &&
        css`
            svg {
                ${BaseStyle.Mixins.fontStyles.ui['18m']}
            }

            ${BaseStyle.Mixins.screenSizes.large} {
                svg {
                    ${BaseStyle.Mixins.fontStyles.ui['24m']}
                }
            }
        `}
`;

export const BottomBar = styled.div<WithColor>`
    display: flex;
    flex-direction: column;
    margin-top: 2px;
    height: 42px;
    width: 100%;
    overflow: hidden;
    container-type: size;

    ${Divider} {
        background-color: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    }

    svg {
        fill: ${({ theme, $colorProps }) => getColor(theme, $colorProps)};
    }

    @media print {
        display: none;
    }
`;

export const GameListContainer = styled.div`
    ${BaseStyle.Mixins.gaps.extraSmall}
    display: flex;
    width: 100%;
    min-height: 102px;
    flex-direction: column;
`;

export const GameContainer = styled.button<{ $isSelectedGame: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 2px 8px;
    gap: 3px;
    background-color: ${({ theme, $isSelectedGame }) =>
        $isSelectedGame ? theme.Surface.secondary : theme.Surface.primary};
`;

export const GameListTime = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['10m']}
    width: 100%;
    height: 10px;
    text-align: left;
    color: ${({ theme }) => theme.Text.primary};
`;

export const SpecialEventImageContainer = styled.div<{ $hasSpecialEventImage: boolean }>`
    height: 72px;
    width: 100%;
    justify-content: center;
    text-align: center;
    margin-top: ${({ $hasSpecialEventImage }) => ($hasSpecialEventImage ? '0' : '10px')};
`;

export const SpecialEventImage = styled.img`
    display: flex;
    height: 100%;
    max-height: 68px;
    width: 100%;
    justify-content: center;
    padding: 0 10px;
    margin-bottom: 2px;
`;

export const NeutralSiteContainer = styled.div<{ $hasSpecialEventImage: boolean }>`
    margin-top: ${({ $hasSpecialEventImage }) => ($hasSpecialEventImage ? '-2px' : '4px')};
`;
